<template>
    <message-standard-details :messageStandardId="props.messageStandardId">
        <div class="col-span-12 lg:col-span-10">
            <page-callout section="form-submission" shadow class="mb-6" />

            <page-header class="mb-2 md:mb-2"> Targets </page-header>

            <page-controls>
                <template v-slot:left>
                    <div class="grow @3xl/controls:max-w-md">
                        <vx-filterable :active="active" @toggle="toggleFilterable" data-test="filters">
                            <template v-slot:placeholder>
                                <span class="mr-2">Find</span>
                                <vx-badge
                                    v-if="filtersCount > 0"
                                    data-test="reset"
                                    :borders="false"
                                    size="xs"
                                    @click.stop="resetFilters"
                                >
                                    {{ pluralized('filter', filtersCount, true) }}
                                    <font-awesome-icon widthAuto :icon="faCircleXmark" />
                                </vx-badge>
                            </template>
                            <template v-slot:form>
                                <form @submit.prevent="applyFilters" class="flex flex-col gap-4">
                                    <div>
                                        <filter-label :canReset="!!filters.query" @reset="filters.query = ''">
                                            Keyword Search
                                        </filter-label>
                                        <vx-input
                                            type="text"
                                            data-test="query"
                                            placeholder="Search"
                                            maxlength="80"
                                            size="lg"
                                            v-model="filters.query"
                                        >
                                            <template v-slot:right>
                                                <font-awesome-icon
                                                    widthAuto
                                                    class="text-xs"
                                                    :icon="faMagnifyingGlass"
                                                />
                                            </template>
                                        </vx-input>
                                    </div>

                                    <div class="flex gap-2 pt-4">
                                        <vx-button
                                            data-test="cancel"
                                            type="button"
                                            color="muted-light"
                                            size="lg"
                                            @click="cancel"
                                        >
                                            Cancel
                                        </vx-button>
                                        <vx-button data-test="apply" type="submit" size="lg" block>
                                            Apply
                                            <font-awesome-icon widthAuto :icon="faCircleCheck"></font-awesome-icon>
                                        </vx-button>
                                    </div>
                                </form>
                            </template>
                        </vx-filterable>
                    </div>
                    <div class="flex w-full gap-2 @xl/controls:w-auto">
                        <vx-button-group size="lg" class="w-full">
                            <vx-select
                                v-model="filters.sort_field"
                                @update:modelValue="applyFilters"
                                :options="[
                                    {
                                        value: 'target_name',
                                        label: 'Sort by: Name',
                                    },
                                    {
                                        value: 'target_id',
                                        label: 'Sort by: External ID',
                                    },
                                ]"
                            ></vx-select>
                            <vx-button
                                @click.prevent="
                                    filters.sort_dir = filters.sort_dir === 'asc' ? 'desc' : 'asc';
                                    applyFilters();
                                "
                                type="button"
                                color="white"
                                shadow
                            >
                                <font-awesome-icon
                                    widthAuto
                                    :icon="filters.sort_dir === 'asc' ? faArrowUpWideShort : faArrowDownWideShort"
                                ></font-awesome-icon>
                            </vx-button>
                        </vx-button-group>
                    </div>
                </template>
                <template v-slot:right>
                    <div class="flex items-center gap-2">
                        <vx-button @click="addTargetModal = true" size="lg" block data-test="add-target-button">
                            Add Target <font-awesome-icon widthAuto :icon="faCirclePlus"></font-awesome-icon>
                        </vx-button>
                    </div>
                </template>
            </page-controls>

            <page-loading v-if="loading"></page-loading>

            <template v-if="!loading && !filteredList.length">
                <vx-box color="info" class="flex flex-col px-8 py-20 gap-6 items-center text-xl text-slate-500">
                    No targets yet.
                    <vx-button @click="addTargetModal = true" data-test="empty-state-add-button">
                        Add Target
                        <font-awesome-icon widthAuto :icon="faCirclePlus"></font-awesome-icon>
                    </vx-button>
                </vx-box>
            </template>

            <vx-table v-else-if="paginatedList.length" class="xl:grid-cols-5-auto" rowBreakpoint="xl">
                <vx-table-row isHeader>
                    <div>Name</div>
                    <div>Type</div>
                    <div>OU</div>
                    <div>Users</div>
                </vx-table-row>

                <message-standard-target-row
                    v-for="item in paginatedList"
                    :key="`${item.target_type}_${item.target_id}`"
                    :modelValue="item"
                    :messageStandardId="props.messageStandardId"
                    @remove="removeTarget"
                />
            </vx-table>

            <div v-if="hasMoreItems" class="mt-4 flex justify-center">
                <vx-button @click="loadMore" color="muted-dark" size="xl" data-test="load-more-btn">
                    Load More
                    <font-awesome-icon widthAuto :icon="faSpinner"></font-awesome-icon>
                </vx-button>
            </div>

            <add-targets-modal
                v-model:visible="addTargetModal"
                :messageStandardId="props.messageStandardId"
                :messageStandardName="messageStandard.name"
                :targetMappings="targetMappings"
                @added="loadTargetMappings"
            />
        </div>
    </message-standard-details>
</template>

<script setup lang="ts">
import {
    faArrowDownWideShort,
    faArrowUpWideShort,
    faCircleCheck,
    faCirclePlus,
    faCircleXmark,
    faMagnifyingGlass,
    faSpinner,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import {
    VxBadge,
    VxBox,
    VxButton,
    VxButtonGroup,
    VxFilterable,
    VxInput,
    VxSelect,
    VxTable,
    VxTableRow,
} from '@voxie/frontend-components';
import { computed, ref, watch } from 'vue';
import { pluralized } from '~/components/filters';
import FilterLabel from '~/components/general/FilterLabel.vue';
import PageControls from '~/components/general/PageControls.vue';
import PageHeader from '~/components/general/PageHeader.vue';
import PageLoading from '~/components/general/PageLoading.vue';
import { useToasted } from '~/composables/useToasted';
import { messageStandardsClient } from '~/services';
import { catchRequestError } from '~/utils/request';
import MessageStandardDetails from '../MessageStandardDetails.vue';
import { useMessageStandardDetails } from '../useMessageStandardDetails';
import MessageStandardTargetRow from './MessageStandardTargetRow.vue';
import PageCallout from '~/components/general/PageCallout.vue';
import AddTargetsModal from './AddTargetsModal.vue';
import type { TargetMappingType } from '~/services/messageStandards/messageStandardsClient.types';

const teamId = Spark.state.currentTeam.id;
const { messageStandard } = useMessageStandardDetails();

const props = defineProps<{ messageStandardId: number }>();

interface TargetRowType extends TargetMappingType {
    team_id: number;
}

const toasted = useToasted();
const addTargetModal = ref(false);
const active = ref(false);
const loading = ref(false);
const targetMappings = ref<TargetMappingType[]>([]);
const page = ref(1);
const ITEMS_PER_PAGE = 15;

const defaultFilters = () => ({
    sort_field: 'target_name' as 'target_name' | 'target_id',
    sort_dir: 'desc' as 'asc' | 'desc',
    query: '',
});

const filters = ref(defaultFilters());
const fetchParams = ref(defaultFilters());

const filtersCount = computed(() => (fetchParams.value.query ? 1 : 0));

const targetsForMessageStandard = computed<TargetRowType[]>(() => {
    return targetMappings.value
        .filter((mapping) => mapping.custom_message_standard_id === props.messageStandardId)
        .map((mapping) => ({
            target_type: mapping.target_type,
            target_id: mapping.target_id,
            target_name: mapping.target_name,
            team_id: teamId,
        }));
});

const filteredList = computed(() => {
    let result = [...targetsForMessageStandard.value];

    if (fetchParams.value.query) {
        const query = fetchParams.value.query.toLowerCase();
        result = result.filter(
            (target) =>
                target.target_name.toLowerCase().includes(query) ||
                String(target.target_id).toLowerCase().includes(query)
        );
    }

    if (fetchParams.value.sort_field) {
        const sortField = fetchParams.value.sort_field;
        const sortDir = fetchParams.value.sort_dir;
        result.sort((a, b) => {
            const aVal = String(a[sortField] ?? '');
            const bVal = String(b[sortField] ?? '');
            const comparison = aVal.localeCompare(bVal);
            return sortDir === 'asc' ? comparison : -comparison;
        });
    }

    return result;
});

const paginatedList = computed(() => filteredList.value.slice(0, page.value * ITEMS_PER_PAGE));

const hasMoreItems = computed(() => page.value * ITEMS_PER_PAGE < filteredList.value.length);

const toggleFilterable = (event: boolean) => {
    active.value = event;
};

const resetFilters = () => {
    filters.value = defaultFilters();
    fetchParams.value = defaultFilters();
    active.value = false;
    page.value = 1;
};

const cancel = async () => {
    filters.value = { ...fetchParams.value };
    active.value = false;
};

const applyFilters = () => {
    fetchParams.value = { ...filters.value };
    active.value = false;
    page.value = 1;
};

const loadMore = () => {
    page.value++;
};

const loadTargetMappings = async () => {
    try {
        const response = await messageStandardsClient.getTargetMappings({ teamId });
        targetMappings.value = response.data.data;
    } catch (e) {
        toasted.error(catchRequestError(e));
    }
};

const removeTarget = async (payload: { target_type: 'group' | 'org_unit'; target_id: string | number }) => {
    try {
        const existingTargets = targetsForMessageStandard.value;

        const remainingGroupIds = existingTargets
            .filter((t) => t.target_type === 'group')
            .filter((t) => !(payload.target_type === 'group' && String(t.target_id) === String(payload.target_id)))
            .map((t) => String(t.target_id));

        const remainingOrgUnitIds = existingTargets
            .filter((t) => t.target_type === 'org_unit')
            .filter((t) => !(payload.target_type === 'org_unit' && Number(t.target_id) === Number(payload.target_id)))
            .map((t) => Number(t.target_id));

        await messageStandardsClient.updateTargets({ teamId }, Number(props.messageStandardId), {
            group_ids: remainingGroupIds,
            org_unit_ids: remainingOrgUnitIds,
        });

        await loadTargetMappings();

        const typeLabel = payload.target_type === 'group' ? 'Group' : 'Organizational Unit';
        toasted.success(`${typeLabel} removed`);
    } catch (e) {
        toasted.error(catchRequestError(e));
    }
};

watch(
    () => props.messageStandardId,
    () => {
        resetFilters();
        loadTargetMappings();
        page.value = 1;
    },
    {
        immediate: true,
    }
);

watch(
    () => fetchParams.value,
    (newValue) => {
        filters.value = { ...newValue };
    },
    {
        deep: true,
        immediate: true,
    }
);
</script>
